import React, { useEffect, useState } from "react";
import { fetchHistory } from "../api/history";
import { SearchHistory } from "../types/search";
import { useAuth } from "../context/AuthContext";

export default function CheckHistory() {
  const { token } = useAuth();
  const [history, setHistory] = useState<SearchHistory[]>([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [selectedQuery, setSelectedQuery] = useState("All");
  const [daMin, setDaMin] = useState<number | "">("");
  const [daMax, setDaMax] = useState<number | "">("");
  const [paMin, setPaMin] = useState<number | "">("");
  const [paMax, setPaMax] = useState<number | "">("");
  const [spamMin, setSpamMin] = useState<number | "">("");
  const [spamMax, setSpamMax] = useState<number | "">("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  useEffect(() => {
  document.title = "Search History • BacklinkBanana";
}, []);


useEffect(() => {
  if (!token) {
    console.log("⛔ No token found, skipping fetchHistory");
    return;
  }

  console.log("🟢 Fetching history with token:", token.slice(0, 10) + "...");
  fetchHistory(token)
    .then((res) => {
      console.log("📜 History API raw response:", res); // 👈 log here
      setHistory(res);
    })
    .catch((err) => console.error("❌ fetchHistory failed:", err))
    .finally(() => setLoading(false));
}, [token]);


  if (loading) return <p className="p-6">Loading history...</p>;
  if (history.length === 0) {
    return (
      <p className="p-6 text-gray-500">No search history in the last 7 days.</p>
    );
  }

  const uniqueQueries = Array.from(new Set(history.map((h) => h.query)));

  // --- Apply filters ---
  const filtered = history.filter((h) => {
    if (selectedQuery !== "All" && h.query !== selectedQuery) return false;

    if (daMin !== "" && h.da < Number(daMin)) return false;
    if (daMax !== "" && h.da > Number(daMax)) return false;

    if (paMin !== "" && h.pa < Number(paMin)) return false;
    if (paMax !== "" && h.pa > Number(paMax)) return false;

    if (spamMin !== "" && h.spam_score < Number(spamMin)) return false;
    if (spamMax !== "" && h.spam_score > Number(spamMax)) return false;

    if (dateFrom) {
      const from = new Date(dateFrom);
      if (new Date(h.created_at) < from) return false;
    }
    if (dateTo) {
      const to = new Date(dateTo);
      if (new Date(h.created_at) > to) return false;
    }

    return true;
  });

  const clearFilters = () => {
    setSelectedQuery("All");
    setDaMin("");
    setDaMax("");
    setPaMin("");
    setPaMax("");
    setSpamMin("");
    setSpamMax("");
    setDateFrom("");
    setDateTo("");
  };

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold mb-4">Search History (Last 7 Days)</h1>

      {/* Info + Download */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-6">
        <p className="text-sm text-gray-600">
          ⚠️ History is automatically deleted after 7 days.
        </p>
        <button
          onClick={() => {
            fetch(`${process.env.REACT_APP_BACKEND_URL}/history/download/excel`, {
              headers: { Authorization: `Bearer ${token}` },
            })
              .then((res) => res.blob())
              .then((blob) => {
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = "history.xlsx";
                a.click();
                window.URL.revokeObjectURL(url);
              });
          }}
          className="mt-2 sm:mt-0 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
        >
          Download Excel
        </button>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        {/* Query */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Query
          </label>
          <select
            value={selectedQuery}
            onChange={(e) => setSelectedQuery(e.target.value)}
            className="w-full border rounded px-2 py-1"
          >
            <option>All</option>
            {uniqueQueries.map((q, i) => (
              <option key={i} value={q}>
                {q}
              </option>
            ))}
          </select>
        </div>

        {/* DA */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            DA Range
          </label>
          <div className="flex gap-2">
            <input
              type="number"
              placeholder="Min"
              value={daMin}
              onChange={(e) =>
                setDaMin(e.target.value ? Number(e.target.value) : "")
              }
              className="w-1/2 border rounded px-2 py-1"
            />
            <input
              type="number"
              placeholder="Max"
              value={daMax}
              onChange={(e) =>
                setDaMax(e.target.value ? Number(e.target.value) : "")
              }
              className="w-1/2 border rounded px-2 py-1"
            />
          </div>
        </div>

        {/* PA */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            PA Range
          </label>
          <div className="flex gap-2">
            <input
              type="number"
              placeholder="Min"
              value={paMin}
              onChange={(e) =>
                setPaMin(e.target.value ? Number(e.target.value) : "")
              }
              className="w-1/2 border rounded px-2 py-1"
            />
            <input
              type="number"
              placeholder="Max"
              value={paMax}
              onChange={(e) =>
                setPaMax(e.target.value ? Number(e.target.value) : "")
              }
              className="w-1/2 border rounded px-2 py-1"
            />
          </div>
        </div>

        {/* Spam */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Spam Range
          </label>
          <div className="flex gap-2">
            <input
              type="number"
              placeholder="Min"
              value={spamMin}
              onChange={(e) =>
                setSpamMin(e.target.value ? Number(e.target.value) : "")
              }
              className="w-1/2 border rounded px-2 py-1"
            />
            <input
              type="number"
              placeholder="Max"
              value={spamMax}
              onChange={(e) =>
                setSpamMax(e.target.value ? Number(e.target.value) : "")
              }
              className="w-1/2 border rounded px-2 py-1"
            />
          </div>
        </div>

        {/* Dates */}
        <div className="md:col-span-2 lg:col-span-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Date Range
          </label>
          <div className="flex gap-2">
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="border rounded px-2 py-1"
            />
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="border rounded px-2 py-1"
            />
          </div>
        </div>
      </div>

      {/* Clear Filters */}
      <div className="mb-6">
        <button
          onClick={clearFilters}
          className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
        >
          Clear Filters
        </button>
      </div>

{/* Table */}
<div className="overflow-x-auto sm:overflow-visible rounded-lg border">
  <table className="min-w-full text-sm sm:whitespace-normal whitespace-nowrap">
    <thead className="bg-gray-100 text-left">
      <tr>
        <th className="p-2">Query</th>
        <th className="p-2">Title</th>
        <th className="p-2">URL</th>
        <th className="p-2 text-center">DA</th>
        <th className="p-2 text-center">PA</th>
        <th className="p-2 text-center">Spam</th>
        <th className="p-2">Date</th>
      </tr>
    </thead>
    <tbody>
      {filtered.map((h, i) => (
        <tr key={i} className="border-t hover:bg-gray-50">
          <td className="p-2">
  {h.query.startsWith("[Smart Filter:") ? (
    <span className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded text-xs font-semibold">
      {h.query}
    </span>
  ) : (
    h.query
  )}
</td>

          <td className="p-2">{h.title || "NA"}</td>
          <td className="p-2">
            {h.url ? (
              <a
                href={h.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:underline break-all"
              >
                {h.url}
              </a>
            ) : (
              "NA"
            )}
          </td>
          <td className="p-2 text-center">{h.da}</td>
          <td className="p-2 text-center">{h.pa}</td>
          <td className="p-2 text-center">{h.spam_score}</td>
          <td className="p-2">
            {new Date(h.created_at).toLocaleString()}
          </td>
        </tr>
      ))}
    </tbody>
  </table>
</div>
{/* Timezone Info */}
<p className="text-xs text-gray-500 mt-3 text-center">
  Times shown in your local timezone:&nbsp;
  {Intl.DateTimeFormat().resolvedOptions().timeZone}
</p>


    </div>
  );
}
